import './index.css';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import classnames from 'classnames';
import { Avatar } from '@hh.ru/magritte-ui-avatar';
import { CrossOutlinedSize24, ExclamationTriangleOutlinedSize32 } from '@hh.ru/magritte-ui-icon/icon';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { VSpacing } from '@hh.ru/magritte-ui-spacing';
import { Text } from '@hh.ru/magritte-ui-typography';
import { FileName } from './FileName.js';
import { FileView } from './FileView.js';
import { Placeholder } from './Placeholder.js';
import { useDropActions } from './useDropActions.js';
import { useDropButtons } from './useDropButtons.js';
import { useFilePreview } from './useFilePreview.js';
import { getAvaliableFileTypes, getUploadIcon, bytes2mb, DEFAULT_ACCEPT, RenderFileInfoDefault } from './utils.js';
import { s as styles } from './upload-CHF-Lmcl.js';
import '@hh.ru/magritte-ui-action';
import '@hh.ru/magritte-ui-divider';
import '@hh.ru/magritte-ui-breakpoint';

const SingleUpload = ({ files, trls, size = 'medium', accept = DEFAULT_ACCEPT, selectFileButton: _selectFileButton, deleteFileButton: _deleteFileButton, replaceImageButton: _replaceImageButton, renderFileInfo = RenderFileInfoDefault, onDropAccepted: onDropAcceptedExternal, onDropRejected: onDropRejectedExternal, onFileDelete: onFileDeleteExternal, 'aria-label': ariaLabel, ...dropzoneProps }) => {
    const bodyUploadRef = useRef(null);
    const uploadType = getAvaliableFileTypes(accept);
    const loadedFile = files.length > 0 ? files[0] : undefined;
    const isLoad = Boolean(loadedFile);
    const isLoading = Boolean(loadedFile?.loading);
    const isInvalid = Boolean(loadedFile?.error);
    const isSizeSmall = size === 'small';
    const isImageType = uploadType === 'image';
    const preview = useFilePreview({ loadedFile, isImageType });
    const usePreview = preview !== 'none';
    useEffect(() => {
        if (!bodyUploadRef.current) {
            return void 0;
        }
        if (isSizeSmall) {
            return void 0;
        }
        // фиксируем ширину, так как после выбора контент поменяется
        const currentElement = bodyUploadRef.current;
        currentElement.style.minHeight = `${bodyUploadRef.current.offsetHeight}px`;
        return () => {
            currentElement.style.minHeight = 'auto';
        };
    }, [isSizeSmall]);
    const { onDropAccepted, onDropRejected, onFileDelete } = useDropActions({
        onDropAccepted: onDropAcceptedExternal,
        onDropRejected: onDropRejectedExternal,
        onFileDelete: onFileDeleteExternal,
    });
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        ...dropzoneProps,
        accept,
        onDropAccepted,
        onDropRejected,
        multiple: false,
    });
    const { selectFileButton, deleteFileButton, replaceImageButton } = useDropButtons({
        selectFileButton: _selectFileButton,
        deleteFileButton: _deleteFileButton,
        replaceImageButton: _replaceImageButton,
        onFileDelete: (event) => {
            onFileDelete(event, loadedFile, 0);
        },
    });
    const backgroundStyles = !isSizeSmall
        ? { backgroundImage: preview !== 'none' ? `url('${preview}')` : preview }
        : {};
    return (jsxs("div", { className: classnames(styles.root, styles[`root_${size}`], { [styles.dragActive]: isDragActive }, { [styles.loading]: isLoading }, { [styles.load]: isLoad }, { [styles.invalid]: isInvalid }), ...getRootProps(), "data-qa": "magritte-single-upload", children: [jsx("input", { ...getInputProps(), "data-qa": "magritte-single-upload-input" }), jsx("div", { className: styles.background, style: backgroundStyles, "data-qa": "magritte-single-upload-background" }), jsxs("div", { className: styles.body, ref: bodyUploadRef, children: [isSizeSmall && (jsxs(Fragment, { children: [loadedFile && (jsx(FileView, { uploadType: uploadType, loadedFile: loadedFile, renderFileInfo: renderFileInfo, onFileDelete: (event) => {
                                    onFileDelete(event, loadedFile, 0);
                                }, preview: preview, "aria-label": ariaLabel || '', isLast: true })), !loadedFile && (jsxs(Fragment, { children: [jsx(Avatar, { mode: "icon", style: "secondary", size: 48, icon: getUploadIcon(uploadType, loadedFile, true), "aria-label": ariaLabel || '' }), jsxs("div", { children: [jsx(Text, { typography: "label-2-regular", children: trls.title }), jsx(VSpacing, { default: 4 }), jsx(Text, { typography: "label-4-regular", style: "secondary", children: trls.subTitle })] }), selectFileButton && jsx("div", { className: styles.smallControls, children: selectFileButton })] }))] })), !isSizeSmall && (jsxs(Fragment, { children: [isLoading && (jsxs("div", { className: styles.loader, children: [jsx(Loader, { size: 64 }), jsx("div", { className: styles.cancel, children: jsx(CrossOutlinedSize24, { initialColor: "accent", onClick: (event) => {
                                                onFileDelete(event, loadedFile, 0);
                                            } }) })] })), isInvalid ? (jsx(ExclamationTriangleOutlinedSize32, { initialColor: "negative" })) : (!usePreview && getUploadIcon(uploadType, loadedFile, false)), jsx(VSpacing, { default: 16 }), loadedFile && (jsxs(Fragment, { children: [!usePreview && (jsxs(Fragment, { children: [jsx(FileName, { file: loadedFile.data, isSizeMedium: true }), jsx(VSpacing, { default: 6 }), jsx(Text, { typography: "label-4-regular", style: "secondary", children: renderFileInfo({
                                                    ...loadedFile,
                                                    fileSizeMB: bytes2mb(loadedFile.data.size),
                                                }) })] })), !isLoading && (!isImageType || isInvalid) && deleteFileButton && (jsxs(Fragment, { children: [jsx(VSpacing, { default: 16 }), deleteFileButton] })), !isLoading &&
                                        isImageType &&
                                        !isInvalid &&
                                        (replaceImageButton || deleteFileButton) && (jsxs("div", { className: styles.imageControls, children: [replaceImageButton, deleteFileButton] }))] })), !loadedFile && jsx(Placeholder, { trls: trls, selectFileButton: selectFileButton })] }))] })] }));
};

export { SingleUpload };
//# sourceMappingURL=SingleUpload.js.map
